import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";
import { fetchOrganizationEmployee } from "../../../Apis/Employee-api";
import { fetchEntity } from "../../../Apis/Entity";
import { fetchFunctionalRolesSpecialaByRole } from "../../../Apis/FunctionalManagment";
import { fetchcities } from "../../../Apis/OrganizationLocation";

const GENDERS = ["Male", "Female", "Other"];
const STATUSES = [
  "Applied",
  "Screening",
  "Shortlisted",
  "Interview",
  "Selected",
  "Rejected",
  "Offer Made",
  "Offer Accepted",
  "Offer Declined",
  "Joined",
  "Withdrawn",
];
const APPLICATION_TYPES = [
  {
    value: "Applied",
    description: "Candidate applied on their own.",
  },
  {
    value: "Invited",
    description: "HR invited the candidate.",
  },
  {
    value: "Walk in",
    description: "Candidate visited the office.",
  },
];

const asList = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    const val = payload?.[key];
    if (Array.isArray(val)) return val;
    if (Array.isArray(val?.data)) return val.data;
  }
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const emptyToNull = (value) => (value === "" || value === undefined ? null : value);
const sliceDate = (value) => (value ? String(value).slice(0, 10) : "");
const today = () => new Date().toISOString().slice(0, 10);

const fileNameFromUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  try {
    const path = value.split("?")[0];
    const name = path.split("/").pop();
    return name ? decodeURIComponent(name) : "";
  } catch {
    return "";
  }
};

const uploadCardSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  height: "100%",
  p: 2,
  borderRadius: "var(--ems-r-md)",
  border: "1px solid var(--ems-border)",
  bgcolor: "var(--ems-surface)",
};

const uploadActionBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "var(--ems-r-sm)",
  boxShadow: "none",
};

const uploadViewBtnSx = {
  bgcolor: "var(--ems-primary-tint)",
  color: "var(--ems-primary)",
  border: "1px solid var(--ems-border)",
  "&:hover": { bgcolor: "var(--ems-primary-tint-2)" },
};

const uploadRemoveBtnSx = {
  bgcolor: "var(--ems-red-tint)",
  color: "var(--ems-red)",
  border: "1px solid transparent",
  "&:hover": { bgcolor: "var(--ems-red-tint)", filter: "brightness(0.97)" },
};

const resolveStoredUrl = (value) => {
  if (!value) return "";
  if (value instanceof File) return URL.createObjectURL(value);
  const raw = String(value);
  if (/^https?:\/\//i.test(raw) || raw.startsWith("blob:")) return raw;
  const path = raw.replace(/^\/+/, "");
  if (path.startsWith("storage/")) return `${MAIN_URL}/${path}`;
  return `${MAIN_URL}/storage/app/public/${path}`;
};

const employeeLabel = (emp) => {
  if (!emp) return "";
  const name =
    [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
    emp.name ||
    emp.full_name ||
    "";
  return emp.employee_code ? `${name} (${emp.employee_code})` : name;
};

const INITIAL_FORM = {
  organization_recruitment_opening_id: "",
  organization_functional_role_id: "",
  organization_functional_role_specialization_id: "",
  organization_recruitment_source_id: "",
  organization_entity_id: "",
  general_city_id: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  date_of_birth: "",
  gender: "",
  phone_number: "",
  alternate_phone_number: "",
  email: "",
  profile_image_url: "",
  resume_url: "",
  communication_marks: "",
  interview_marks: "",
  technical_round_marks: "",
  current_organization_name: "",
  current_designation: "",
  total_experience_years: "",
  current_salary: "",
  expected_salary: "",
  current_stipend: "",
  expected_stipend: "",
  notice_period_days: "",
  available_from: "",
  application_date: today(),
  application_status: "Applied",
  application_type: "Applied",
  is_previous_employee: false,
  previous_employee_id: "",
  remarks: "",
};

function RecruitmentApplicationsForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit" || mode === "view");
  const [btnLoading, setBtnLoading] = useState(false);
  const [openings, setOpenings] = useState([]);
  const [functionalRoles, setFunctionalRoles] = useState([]);
  const [roleSpec, setRoleSpec] = useState([]);
  const [sources, setSources] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [entities, setEntities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [cityInput, setCityInput] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [clearProfileImage, setClearProfileImage] = useState(false);
  const [clearResume, setClearResume] = useState(false);

  useEffect(() => {
    if (!org?.organization_id) return;
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

    Promise.allSettled([
      axios.get(`${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-opening`, { headers }),
      axios.get(`${MAIN_URL}/api/organizations/${org.organization_id}/func-role?per_page=all`, { headers }),
      axios.get(`${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source?active_only=1`, { headers }),
      fetchOrganizationEmployee(org.organization_id),
      fetchEntity(org.organization_id),
    ]).then(([openingRes, roleRes, sourceRes, empRes, entityRes]) => {
      if (openingRes.status === "fulfilled") {
        setOpenings(asList(openingRes.value?.data, ["recruitment_openings", "data"]));
      }
      if (roleRes.status === "fulfilled") {
        setFunctionalRoles(asList(roleRes.value?.data, ["functional", "data"]));
      }
      if (sourceRes.status === "fulfilled") {
        setSources(asList(sourceRes.value?.data, ["recruitment_sources", "data"]));
      }
      if (empRes.status === "fulfilled") {
        setEmployees(asList(empRes.value, ["employees", "Employees", "data"]));
      }
      if (entityRes.status === "fulfilled") {
        setEntities(asList(entityRes.value, ["entity", "entities", "data"]));
      }
    });
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id || !formData.organization_functional_role_id) {
      setRoleSpec([]);
      return;
    }

    fetchFunctionalRolesSpecialaByRole(
      org.organization_id,
      formData.organization_functional_role_id
    )
      .then((data) => {
        const specs = data?.functional ?? [];
        setRoleSpec(Array.isArray(specs) ? specs : []);
      })
      .catch((err) => {
        console.error("Error fetching role specializations:", err);
        setRoleSpec([]);
      });
  }, [org?.organization_id, formData.organization_functional_role_id]);

  useEffect(() => {
    if (!cityInput || cityInput.trim().length < 2) return;
    let cancelled = false;
    fetchcities(cityInput.trim())
      .then((data) => {
        if (!cancelled) setCityOptions(Array.isArray(data?.cities) ? data.cities : []);
      })
      .catch((err) => {
        console.error("Error fetching cities:", err);
        if (!cancelled) setCityOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [cityInput]);

  useEffect(() => {
    const loadById = async () => {
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-application/${id}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        const a = response?.data?.recruitment_application || {};
        setFormData({
          ...INITIAL_FORM,
          organization_recruitment_opening_id: a.organization_recruitment_opening_id || "",
          organization_functional_role_id: a.organization_functional_role_id || "",
          organization_functional_role_specialization_id:
            a.organization_functional_role_specialization_id || "",
          organization_recruitment_source_id: a.organization_recruitment_source_id || "",
          organization_entity_id: a.organization_entity_id || "",
          general_city_id: a.general_city_id || "",
          first_name: a.first_name || "",
          middle_name: a.middle_name || "",
          last_name: a.last_name || "",
          date_of_birth: sliceDate(a.date_of_birth),
          gender: a.gender || "",
          phone_number: a.phone_number || "",
          alternate_phone_number: a.alternate_phone_number || "",
          email: a.email || "",
          profile_image_url: a.profile_image_url || "",
          resume_url: a.resume_url || "",
          communication_marks: a.communication_marks ?? "",
          interview_marks: a.interview_marks ?? "",
          technical_round_marks: a.technical_round_marks ?? "",
          current_organization_name: a.current_organization_name || "",
          current_designation: a.current_designation || "",
          total_experience_years: a.total_experience_years ?? "",
          current_salary: a.current_salary ?? "",
          expected_salary: a.expected_salary ?? "",
          current_stipend: a.current_stipend ?? "",
          expected_stipend: a.expected_stipend ?? "",
          notice_period_days: a.notice_period_days ?? "",
          available_from: sliceDate(a.available_from),
          application_date: sliceDate(a.application_date) || today(),
          application_status: a.application_status || "Applied",
          application_type: a.application_type || "Applied",
          is_previous_employee: a.is_previous_employee == 1 || a.is_previous_employee === true,
          previous_employee_id: a.previous_employee_id || "",
          remarks: a.remarks || "",
        });
        if (a.general_city_id && a.city_name) {
          const cityOpt = {
            general_city_id: a.general_city_id,
            city_name: a.city_name,
          };
          setSelectedCity(cityOpt);
          setCityOptions([cityOpt]);
          setCityInput(a.city_name);
        } else {
          setSelectedCity(null);
          setCityInput("");
        }
        setProfileImageFile(null);
        setResumeFile(null);
        setClearProfileImage(false);
        setClearResume(false);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load application");
      } finally {
        setLoading(false);
      }
    };

    if ((mode === "edit" || mode === "view") && id && org?.organization_id) {
      setLoading(true);
      loadById();
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setProfileImageFile(file);
    setClearProfileImage(false);
    setFormErrors((prev) => ({ ...prev, profile_image_url: "" }));
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setResumeFile(file);
    setClearResume(false);
    setFormErrors((prev) => ({ ...prev, resume_url: "" }));
  };

  const handleRemoveProfileImage = () => {
    setProfileImageFile(null);
    setClearProfileImage(true);
    setFormData((prev) => ({ ...prev, profile_image_url: "" }));
  };

  const handleRemoveResume = () => {
    setResumeFile(null);
    setClearResume(true);
    setFormData((prev) => ({ ...prev, resume_url: "" }));
  };

  const profilePreviewUrl = useMemo(() => {
    if (profileImageFile) return URL.createObjectURL(profileImageFile);
    if (clearProfileImage) return "";
    return resolveStoredUrl(formData.profile_image_url);
  }, [profileImageFile, clearProfileImage, formData.profile_image_url]);

  const resumePreviewUrl = useMemo(() => {
    if (resumeFile) return URL.createObjectURL(resumeFile);
    if (clearResume) return "";
    return resolveStoredUrl(formData.resume_url);
  }, [resumeFile, clearResume, formData.resume_url]);

  const resumeDisplayName = useMemo(() => {
    if (resumeFile?.name) return resumeFile.name;
    if (clearResume) return "";
    return fileNameFromUrl(formData.resume_url) || (formData.resume_url ? "Resume on file" : "");
  }, [resumeFile, clearResume, formData.resume_url]);

  useEffect(() => {
    return () => {
      if (profilePreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(profilePreviewUrl);
    };
  }, [profilePreviewUrl]);

  useEffect(() => {
    return () => {
      if (resumePreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(resumePreviewUrl);
    };
  }, [resumePreviewUrl]);

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name?.trim()) errors.first_name = "First name is required.";
    if (!formData.last_name?.trim()) errors.last_name = "Last name is required.";
    if (!formData.phone_number?.trim()) errors.phone_number = "Phone number is required.";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Enter a valid email.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    const body = new FormData();
    const fields = {
      organization_recruitment_opening_id: formData.organization_recruitment_opening_id || "",
      organization_functional_role_id: formData.organization_functional_role_id || "",
      organization_functional_role_specialization_id: emptyToNull(
        formData.organization_functional_role_specialization_id
      ),
      organization_recruitment_source_id: emptyToNull(formData.organization_recruitment_source_id),
      organization_entity_id: emptyToNull(formData.organization_entity_id),
      general_city_id: emptyToNull(formData.general_city_id),
      first_name: formData.first_name,
      middle_name: emptyToNull(formData.middle_name),
      last_name: formData.last_name,
      date_of_birth: emptyToNull(formData.date_of_birth),
      gender: emptyToNull(formData.gender),
      phone_number: formData.phone_number,
      alternate_phone_number: emptyToNull(formData.alternate_phone_number),
      email: emptyToNull(formData.email),
      communication_marks: emptyToNull(formData.communication_marks),
      interview_marks: emptyToNull(formData.interview_marks),
      technical_round_marks: emptyToNull(formData.technical_round_marks),
      current_organization_name: emptyToNull(formData.current_organization_name),
      current_designation: emptyToNull(formData.current_designation),
      total_experience_years: emptyToNull(formData.total_experience_years),
      current_salary: emptyToNull(formData.current_salary),
      expected_salary: emptyToNull(formData.expected_salary),
      current_stipend: emptyToNull(formData.current_stipend),
      expected_stipend: emptyToNull(formData.expected_stipend),
      notice_period_days: emptyToNull(formData.notice_period_days),
      available_from: emptyToNull(formData.available_from),
      application_date: formData.application_date || today(),
      application_status: formData.application_status || "Applied",
      application_type: formData.application_type || "Applied",
      is_previous_employee: formData.is_previous_employee ? "1" : "0",
      previous_employee_id: formData.is_previous_employee
        ? emptyToNull(formData.previous_employee_id)
        : null,
      remarks: emptyToNull(formData.remarks),
    };

    Object.entries(fields).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        body.append(key, value);
      }
    });

    if (profileImageFile) {
      body.append("profile_image_url", profileImageFile);
    } else if (clearProfileImage) {
      body.append("clear_profile_image", "1");
    }

    if (resumeFile) {
      body.append("resume_url", resumeFile);
    } else if (clearResume) {
      body.append("clear_resume", "1");
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      if (mode === "edit") {
        body.append("_method", "PUT");
        await axios.post(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-application/${id}`,
          body,
          { headers }
        );
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-application`,
          body,
          { headers }
        );
      }
      toast.success(mode === "edit" ? "Application Updated!" : "Application Created!");
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages = Object.values(validationErrors)
          .map((arr) => (Array.isArray(arr) ? arr[0] : arr))
          .join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Header
      mode={mode}
      updateMessage={"Recruitment Application"}
      addMessage={"Recruitment Application"}
      homeLink={"/recruitment/applications"}
      homeText={"Recruitment Applications"}
      onSubmit={handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={10}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700}>Application</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={openings}
                    getOptionLabel={(o) => o.opening_title || ""}
                    value={openings.find((o) => String(o.organization_recruitment_opening_id) === String(formData.organization_recruitment_opening_id)) || null}
                    onChange={(_, newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        organization_recruitment_opening_id: newValue?.organization_recruitment_opening_id || "",
                        organization_entity_id:
                          newValue?.organization_entity_id || prev.organization_entity_id || "",
                      }))
                    }
                    disabled={mode === "view"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Opening"
                        error={!!formErrors.organization_recruitment_opening_id}
                        helperText={formErrors.organization_recruitment_opening_id}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={functionalRoles}
                    getOptionLabel={(o) =>
                      o.functional_role_code
                        ? `${o.functional_role_name} (${o.functional_role_code})`
                        : o.functional_role_name || ""
                    }
                    isOptionEqualToValue={(a, b) =>
                      String(a.organization_functional_role_id) === String(b.organization_functional_role_id)
                    }
                    value={
                      functionalRoles.find(
                        (o) =>
                          String(o.organization_functional_role_id) ===
                          String(formData.organization_functional_role_id)
                      ) || null
                    }
                    onChange={(_, newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        organization_functional_role_id:
                          newValue?.organization_functional_role_id || "",
                        organization_functional_role_specialization_id: "",
                      }))
                    }
                    disabled={mode === "view"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Functional Role"
                        error={!!formErrors.organization_functional_role_id}
                        helperText={formErrors.organization_functional_role_id || ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={roleSpec}
                    getOptionLabel={(o) =>
                      o.functional_role_specialization_code
                        ? `${o.functional_role_specialization_name} (${o.functional_role_specialization_code})`
                        : o.functional_role_specialization_name || ""
                    }
                    isOptionEqualToValue={(a, b) =>
                      String(a.organization_functional_role_specialization_id) ===
                      String(b.organization_functional_role_specialization_id)
                    }
                    value={
                      roleSpec.find(
                        (o) =>
                          String(o.organization_functional_role_specialization_id) ===
                          String(formData.organization_functional_role_specialization_id)
                      ) || null
                    }
                    onChange={(_, newValue) =>
                      setField(
                        "organization_functional_role_specialization_id",
                        newValue?.organization_functional_role_specialization_id || ""
                      )
                    }
                    disabled={mode === "view" || !formData.organization_functional_role_id}
                    noOptionsText={
                      formData.organization_functional_role_id
                        ? "No specializations for this role"
                        : "Select a functional role first"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Functional Role Specialization"
                        error={!!formErrors.organization_functional_role_specialization_id}
                        helperText={
                          formErrors.organization_functional_role_specialization_id || ""
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={sources}
                    getOptionLabel={(o) => o.recruitment_source_name || o.label || ""}
                    value={sources.find((o) => String(o.organization_recruitment_source_id) === String(formData.organization_recruitment_source_id)) || null}
                    onChange={(_, newValue) => setField("organization_recruitment_source_id", newValue?.organization_recruitment_source_id || "")}
                    disabled={mode === "view"}
                    renderInput={(params) => <TextField {...params} label="Recruitment Source" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Application Type"
                    name="application_type"
                    disabled={mode === "view"}
                    value={formData.application_type || "Applied"}
                    onChange={handleChange}
                    helperText={
                      APPLICATION_TYPES.find((t) => t.value === formData.application_type)
                        ?.description || ""
                    }
                  >
                    {APPLICATION_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>Organization Mapping</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={entities}
                    getOptionLabel={(o) => o.entity_name || ""}
                    isOptionEqualToValue={(a, b) =>
                      String(a.organization_entity_id) === String(b.organization_entity_id)
                    }
                    value={
                      entities.find(
                        (o) => String(o.organization_entity_id) === String(formData.organization_entity_id)
                      ) || null
                    }
                    onChange={(_, newValue) => setField("organization_entity_id", newValue?.organization_entity_id || "")}
                    disabled={mode === "view"}
                    renderInput={(params) => <TextField {...params} label="Entity" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth label="Status" name="application_status" disabled={mode === "view"} value={formData.application_status} onChange={handleChange}>
                    {STATUSES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Application Date" name="application_date" disabled={mode === "view"} value={formData.application_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Available From" name="available_from" disabled={mode === "view"} value={formData.available_from} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>Applicant Details</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth required label="First Name" name="first_name" disabled={mode === "view"} value={formData.first_name} onChange={handleChange} error={!!formErrors.first_name} helperText={formErrors.first_name} inputProps={{ maxLength: 30 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Middle Name" name="middle_name" disabled={mode === "view"} value={formData.middle_name} onChange={handleChange} inputProps={{ maxLength: 30 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth required label="Last Name" name="last_name" disabled={mode === "view"} value={formData.last_name} onChange={handleChange} error={!!formErrors.last_name} helperText={formErrors.last_name} inputProps={{ maxLength: 30 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={cityOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option?.city_name || ""
                    }
                    isOptionEqualToValue={(a, b) =>
                      String(a?.general_city_id) === String(b?.general_city_id)
                    }
                    value={selectedCity}
                    onChange={(_, newValue) => {
                      setSelectedCity(newValue);
                      setField("general_city_id", newValue?.general_city_id || "");
                      if (newValue?.city_name) setCityInput(newValue.city_name);
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input") setCityInput(newInputValue);
                      if (reason === "clear") {
                        setCityInput("");
                        setSelectedCity(null);
                        setField("general_city_id", "");
                      }
                    }}
                    disabled={mode === "view"}
                    filterOptions={(x) => x}
                    noOptionsText={
                      cityInput.trim().length < 2
                        ? "Type at least 2 characters"
                        : "No cities found"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        helperText="Shown under applicant name in the list"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Date of Birth" name="date_of_birth" disabled={mode === "view"} value={formData.date_of_birth} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth label="Gender" name="gender" disabled={mode === "view"} value={formData.gender} onChange={handleChange}>
                    <MenuItem value="">None</MenuItem>
                    {GENDERS.map((g) => (
                      <MenuItem key={g} value={g}>{g}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="number" label="Total Experience (Years)" name="total_experience_years" disabled={mode === "view"} value={formData.total_experience_years} onChange={handleChange} inputProps={{ min: 0, step: 0.1 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth required label="Phone Number" name="phone_number" disabled={mode === "view"} value={formData.phone_number} onChange={handleChange} error={!!formErrors.phone_number} helperText={formErrors.phone_number} inputProps={{ maxLength: 20 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Alternate Phone" name="alternate_phone_number" disabled={mode === "view"} value={formData.alternate_phone_number} onChange={handleChange} inputProps={{ maxLength: 20 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Email" name="email" disabled={mode === "view"} value={formData.email} onChange={handleChange} error={!!formErrors.email} helperText={formErrors.email} inputProps={{ maxLength: 100 }} />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      alignItems: "stretch",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ ...uploadCardSx, flex: 1, minWidth: 0 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "var(--ems-text)", mb: 0.25 }}
                        >
                          Profile Photo
                        </Typography>
                        <Typography variant="caption" sx={{ color: "var(--ems-faint)" }}>
                          JPG, PNG, or WEBP
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
                        <Avatar
                          src={profilePreviewUrl || undefined}
                          alt="Profile"
                          sx={{
                            width: 80,
                            height: 80,
                            bgcolor: "var(--ems-surface-2)",
                            color: "var(--ems-muted)",
                            border: "1px solid var(--ems-border)",
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
                          {profileImageFile?.name && (
                            <Typography
                              variant="body2"
                              noWrap
                              title={profileImageFile.name}
                              sx={{ color: "var(--ems-green)", fontWeight: 600 }}
                            >
                              {profileImageFile.name}
                            </Typography>
                          )}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              disabled={mode === "view"}
                              variant="contained"
                              component="label"
                              size="small"
                              sx={uploadActionBtnSx}
                            >
                              {profilePreviewUrl ? "Change Photo" : "Upload Photo"}
                              <input
                                type="file"
                                hidden
                                accept="image/jpeg,image/png,image/webp"
                                onChange={handleProfileImageChange}
                              />
                            </Button>
                            {profilePreviewUrl && (
                              <IconButton
                                size="small"
                                aria-label="View profile photo"
                                onClick={() => window.open(profilePreviewUrl, "_blank")}
                                sx={uploadViewBtnSx}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            )}
                            {mode !== "view" && profilePreviewUrl && (
                              <IconButton
                                size="small"
                                aria-label="Remove profile photo"
                                onClick={handleRemoveProfileImage}
                                sx={uploadRemoveBtnSx}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      {formErrors.profile_image_url && (
                        <Typography variant="caption" color="error">
                          {Array.isArray(formErrors.profile_image_url)
                            ? formErrors.profile_image_url[0]
                            : formErrors.profile_image_url}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ ...uploadCardSx, flex: 1, minWidth: 0 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "var(--ems-text)", mb: 0.25 }}
                        >
                          Resume
                        </Typography>
                        <Typography variant="caption" sx={{ color: "var(--ems-faint)" }}>
                          PDF, DOC, or DOCX
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "var(--ems-r-md)",
                            bgcolor: resumePreviewUrl
                              ? "var(--ems-primary-tint)"
                              : "var(--ems-surface-2)",
                            color: resumePreviewUrl
                              ? "var(--ems-primary)"
                              : "var(--ems-muted)",
                            border: "1px solid var(--ems-border)",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          <DescriptionOutlinedIcon sx={{ fontSize: 36 }} />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={resumeDisplayName || undefined}
                            sx={{
                              color: resumeDisplayName ? "var(--ems-green)" : "var(--ems-faint)",
                              fontWeight: resumeDisplayName ? 600 : 500,
                            }}
                          >
                            {resumeDisplayName || "No file selected"}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              disabled={mode === "view"}
                              variant="contained"
                              component="label"
                              size="small"
                              sx={uploadActionBtnSx}
                            >
                              {resumePreviewUrl ? "Change Resume" : "Upload Resume"}
                              <input
                                type="file"
                                hidden
                                accept=".pdf,.doc,.docx,application/pdf"
                                onChange={handleResumeChange}
                              />
                            </Button>
                            {resumePreviewUrl && (
                              <IconButton
                                size="small"
                                aria-label="View resume"
                                onClick={() => window.open(resumePreviewUrl, "_blank")}
                                sx={uploadViewBtnSx}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            )}
                            {mode !== "view" && resumePreviewUrl && (
                              <IconButton
                                size="small"
                                aria-label="Remove resume"
                                onClick={handleRemoveResume}
                                sx={uploadRemoveBtnSx}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      {formErrors.resume_url && (
                        <Typography variant="caption" color="error">
                          {Array.isArray(formErrors.resume_url)
                            ? formErrors.resume_url[0]
                            : formErrors.resume_url}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>Assessment</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Communication"
                    name="communication_marks"
                    disabled={mode === "view"}
                    value={formData.communication_marks}
                    onChange={handleChange}
                    helperText="Out of 10"
                    inputProps={{ min: 0, max: 10, step: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Interview"
                    name="interview_marks"
                    disabled={mode === "view"}
                    value={formData.interview_marks}
                    onChange={handleChange}
                    helperText="Out of 10"
                    inputProps={{ min: 0, max: 10, step: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Technical Round"
                    name="technical_round_marks"
                    disabled={mode === "view"}
                    value={formData.technical_round_marks}
                    onChange={handleChange}
                    helperText="Out of 10"
                    inputProps={{ min: 0, max: 10, step: 0.5 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>Current Employment</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Current Organization" name="current_organization_name" disabled={mode === "view"} value={formData.current_organization_name} onChange={handleChange} inputProps={{ maxLength: 100 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Current Designation" name="current_designation" disabled={mode === "view"} value={formData.current_designation} onChange={handleChange} inputProps={{ maxLength: 100 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Current Salary" name="current_salary" disabled={mode === "view"} value={formData.current_salary} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Expected Salary" name="expected_salary" disabled={mode === "view"} value={formData.expected_salary} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Current Stipend" name="current_stipend" disabled={mode === "view"} value={formData.current_stipend} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Expected Stipend" name="expected_stipend" disabled={mode === "view"} value={formData.expected_stipend} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="number" label="Notice Period (Days)" name="notice_period_days" disabled={mode === "view"} value={formData.notice_period_days} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.is_previous_employee}
                        disabled={mode === "view"}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_previous_employee: e.target.checked,
                            previous_employee_id: e.target.checked ? prev.previous_employee_id : "",
                          }))
                        }
                      />
                    }
                    label="Previous Employee"
                  />
                </Grid>
                {formData.is_previous_employee && (
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={employees}
                      getOptionLabel={employeeLabel}
                      value={employees.find((e) => String(e.employee_id) === String(formData.previous_employee_id)) || null}
                      onChange={(_, newValue) => setField("previous_employee_id", newValue?.employee_id || "")}
                      disabled={mode === "view"}
                      renderInput={(params) => <TextField {...params} label="Previous Employee" />}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={3} label="Remarks" name="remarks" disabled={mode === "view"} value={formData.remarks} onChange={handleChange} inputProps={{ maxLength: 255 }} />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleSubmit}
                    disabled={loading || btnLoading || mode === "view"}
                    sx={{ borderRadius: 2, minWidth: 120, textTransform: "capitalize", fontWeight: 500, mt: 1 }}
                  >
                    {btnLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                  </Button>
                  {mode === "edit" && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate(-1)}
                      sx={{ borderRadius: 2, minWidth: 120, textTransform: "capitalize", fontWeight: 500, ml: 2, mt: 1 }}
                    >
                      Cancel
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default RecruitmentApplicationsForm;
